import {
	ClientSideDataGridModel,
	ClientSideDataGridModelRenderProps,
} from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { InlineCrudDataGrid, InlineCrudDataGridApi } from "src/components/common/dataGrid/crud/InlineCrudDataGrid.tsx";
import {
	SitePartRevisionOperationsDataGridApi,
	SitePartRevisionOperationsDataGridApi_Data,
} from "src/api/generated/erp/parts/sitePart/api/bom/sitePartRevisionOperationsDataGridApi.ts";
import { autoCompleteSingleSelectColumn, floatColumn, integerColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { concatWithPipe, isNullOrBlank } from "src/utils/strings.tsx";
import { AavoDataGridApi } from "src/components/common/dataGrid/AavoDataGridApi.ts";
import { associate, filterNulls } from "src/utils/arrayUtils.ts";
import { PartRevisionOperationView } from "src/api/generated/erp/db/types/tables/partRevisionOperationView.ts";
import { GridBooleanCell } from "@mui/x-data-grid-pro";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { faAngleDown, faAngleUp, faLaptopCode } from "@fortawesome/pro-regular-svg-icons";
import { AavoIconButton } from "src/components/common/buttons/AavoIconButton.tsx";
import { useRef } from "react";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { FormConfiguratorLuaEditor } from "src/components/views/erp/configurator/scripting/FormConfiguratorLuaEditor.tsx";
import { SitePartRevisionUnrestrictedEditingWarning } from "src/components/views/erp/parts/siteParts/SitePartRevisionUnrestrictedEditingWarning.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SitePartRevisionOperationsDataGridProps {
	partRevisionId: number;
	dataDirtyStateChanged?: DataDirtyStateChangeHandler;
	readOnly?: boolean;
}

export const SitePartRevisionOperationsDataGrid = (props: SitePartRevisionOperationsDataGridProps) => {
	const { partRevisionId } = props;
	return (
		<ClientSideDataGridModel
			fetchData={() => SitePartRevisionOperationsDataGridApi.getData({ partRevisionId })}
			gridId={"9ADC38DC6632BEF6"}
			getRows={(data) => data.partRevisionOperations}
			initialParams={{}}
			getRowId={(row) => row.partRevisionOperationId!}
			customFunctionUiActionView={"PART_REVISION_OPERATIONS"}
			render={(params) => <AsyncContent {...params} {...props} />}
		/>
	);
};

interface AsyncContentProps
	extends SitePartRevisionOperationsDataGridProps,
		ClientSideDataGridModelRenderProps<
			SitePartRevisionOperationsDataGridApi_Data,
			PartRevisionOperationView,
			Record<string, never>
		> {}

const AsyncContent = ({
	dataGridProps,
	getApi,
	onlySelectedRow,
	dataDirtyStateChanged,
	refreshData,
	readOnly,
	data: { operationOptions, partRevision },
}: AsyncContentProps) => {
	const showFormDialog = useFormInput();
	const inlineCrudGridApiRef = useRef<InlineCrudDataGridApi | null>(null);

	const { partRevisionId, partRevisionState, partIsConfigurable } = partRevision;
	const editable = !readOnly && (partRevisionState !== "RELEASED" || partRevision.unrestrictedEditingCascaded);
	const operationsByIds = associate(
		operationOptions,
		(op) => op.operationId,
		(op) => op,
	);
	return (
		<VerticalBox sx={{ flex: 1 }}>
			<SitePartRevisionUnrestrictedEditingWarning revision={partRevision} />
			<InlineCrudDataGrid<PartRevisionOperationView>
				inlineCrudGridApiRef={inlineCrudGridApiRef}
				dataDirtyStateChanged={dataDirtyStateChanged}
				editMode={"cell"}
				columns={[
					integerColumn({
						field: "position",
						headerName: i18n.t("number_shortened"),
					}),
					autoCompleteSingleSelectColumn({
						field: "operationId",
						headerName: i18n.t("operation"),
						editable: true,
						width: 200,
						validate: "required",
						disableClearable: true,
						valueOptions: ({ id }) => {
							const currentOperationId = id == null ? null : getApi().getRow(id)?.operationId;
							const usedOperationIds = getApi()
								.getRows()
								.filter((r) => r.operationId != null)
								.map((r) => r.operationId);

							return operationOptions.filter((op) => {
								if (op.operationId === currentOperationId) return true;
								return !usedOperationIds.includes(op.operationId) && op.isActive;
							});
						},
						getOptionValue: (op) => op.operationId,
						getOptionLabel: (op) => concatWithPipe(op.operationNo, op.operationDescription),
						onChange: ({ row, option }) => {
							// Set material operation to selected operation if not set
							if (row.materialOperationOperationId == null) {
								inlineCrudGridApiRef.current?.updateRows([
									{
										...row,
										materialOperationOperationId: option.operationId,
									},
								]);
							}
						},
					}),
					autoCompleteSingleSelectColumn({
						field: "materialOperationOperationId",
						headerName: i18n.t("material_operation"),
						editable: true,
						width: 200,
						validate: "required",
						valueOptions: ({ id }) =>
							filterNulls(
								getApi()
									.getRows()
									.filter((r) => r.operationId != null)
									// Show self row first.
									.sort((a, b) => {
										if (getApi().getRowId(a) === id) return -1;
										if (getApi().getRowId(b) === id) return 1;
										return a.position - b.position;
									}),
							),
						getOptionValue: ({ operationId }) => operationId,
						getOptionLabel: ({ operationId }) => {
							const operation = operationsByIds[operationId];
							if (operation == null) return "";
							return concatWithPipe(operation.operationNo, operation.operationDescription);
						},
					}),
					partIsConfigurable && {
						type: "boolean",
						field: "hasConfigurationRule",
						headerName: i18n.t("configuration_rule"),
						width: 150,
						valueGetter: (_, row) => !isNullOrBlank(row.configuratorTransformationScript),
						renderCell: (params) => (
							<HorizontalBox sx={{ alignItems: "center", gap: 0.5 }}>
								<GridBooleanCell {...params} />
								<AavoIconButton
									icon={faLaptopCode}
									size={"small"}
									tooltip={
										params.row.partRevisionOperationId == null ?
											i18n.t("save_row_first")
										:	i18n.t("configuration_rule")
									}
									disabled={params.row.partRevisionOperationId == null}
									onClick={() => openTransformationScriptEditor(params.row)}
								/>
							</HorizontalBox>
						),
					},
					floatColumn({
						field: "setUpTime",
						headerName: i18n.t("set_up_time_hours"),
						editable: true,
						width: 150,
					}),
					floatColumn({
						field: "workingTime",
						headerName: i18n.t("working_time_hours"),
						editable: true,
						width: 150,
					}),
				]}
				add={
					editable ?
						{
							type: "enabled",
							action: () => ({
								partRevisionId: partRevisionId,
								position: getDefaultPosition(getApi()),
							}),
						}
					:	{ type: "hidden" }
				}
				save={
					editable ?
						{
							type: "enabled",
							action: async ({ items }) => {
								return await SitePartRevisionOperationsDataGridApi.save({
									partRevisionId: partRevisionId,
									partRevisionOperations: items,
								});
							},
						}
					:	{ type: "hidden" }
				}
				remove={
					editable ?
						{
							type: "enabled",
							action: async ({ items }) => {
								return await SitePartRevisionOperationsDataGridApi.delete_({
									partRevisionId: partRevisionId,
									partRevisionOperations: items,
								});
							},
						}
					:	{ type: "hidden" }
				}
				actionBarComponents={
					<>
						<AsyncButton
							icon={faAngleUp}
							disabled={!onlySelectedRow}
							onClick={async () => {
								await SitePartRevisionOperationsDataGridApi.movePosition({
									partRevisionOperationId: onlySelectedRow!.partRevisionOperationId,
									direction: "UP",
								});
								await refreshData();
							}}
						/>
						<AsyncButton
							icon={faAngleDown}
							disabled={!onlySelectedRow}
							onClick={async () => {
								await SitePartRevisionOperationsDataGridApi.movePosition({
									partRevisionOperationId: onlySelectedRow!.partRevisionOperationId,
									direction: "DOWN",
								});
								await refreshData();
							}}
						/>
						{partIsConfigurable && (
							<AsyncButton
								variant={"outlined"}
								icon={faLaptopCode}
								label={i18n.t("configuration_rule")}
								disabled={!onlySelectedRow}
								onClick={async () => {
									if (!onlySelectedRow) return;
									// Cannot use onlySelectedRow directly, because it hasn't local modifications
									const internalRow = getApi().getRow(onlySelectedRow.partRevisionOperationId);
									if (internalRow == null) return;
									await openTransformationScriptEditor(internalRow);
								}}
							/>
						)}
					</>
				}
				agentContext={{
					key: defineAgentUiContextKey("erp.parts.sitePartRevisionOperations.grid"),
					data: {
						partRevisionId,
						readOnly: readOnly ?? false,
					},
					rowFields: ["partRevisionOperationId", "partRevisionId", "operationId", "position"],
					selectedRowPathSegment: (row) => `${i18n.t("operation")} ${row.position}`,
				}}
				{...dataGridProps}
			/>
		</VerticalBox>
	);

	async function openTransformationScriptEditor(row: PartRevisionOperationView) {
		if (row.partRevisionOperationId == null) return;

		const formResult = await showFormDialog<{ script: string }>({
			title: i18n.t("configuration_rule"),
			size: "xl",
			defaultValues: {
				script: row.configuratorTransformationScript,
			},
			disabled: !editable,
			content: ({ control }) => (
				<FormConfiguratorLuaEditor
					control={control}
					name={"script"}
					productFamilyVersionId={partRevision.configurationProductFamilyVersionId}
					catalogPartRevisionId={partRevision.catalogPartRevisionId}
				/>
			),
		});
		if (formResult === undefined) return;

		await SitePartRevisionOperationsDataGridApi.saveConfiguratorTransformationScript({
			partRevisionOperationId: row.partRevisionOperationId,
			script: formResult.script,
		});

		if (inlineCrudGridApiRef.current == null) throw Error("inlineCrudGridApiRef is null");
		inlineCrudGridApiRef.current.updateRows([
			{
				...row,
				configuratorTransformationScript: formResult.script,
			},
		]);
	}

	function getDefaultPosition(api: AavoDataGridApi<PartRevisionOperationView>): number {
		const currentPositions = api.getRows().map((r) => r.position);
		if (currentPositions.length === 0) return 1;
		return Math.max(...currentPositions) + 1;
	}
};
